import { businessPages } from '@/pages/business/routes';
import type { PlatformPermission } from '@/services/business/permissions';
import { hasPlatformPermission } from '@/services/business/permissions';
import { lazyPage } from '@/utils/lazyPage';
import {
  defaultRoute,
  templateNavigationItems,
  templatePageItems,
} from './templateNavigation';
import type { AppAccess, AppMenuItem, AppRouteConfig, AppRouteMeta } from './types';

export { defaultRoute };

const accountPageItems: AppMenuItem[] = [
  {
    key: '/account/center',
    path: '/account/center',
    name: '个人中心',
    nameEn: 'Personal center',
    pageKind: 'account-profile',
  },
  {
    key: '/account/settings',
    path: '/account/settings',
    name: '个人设置',
    nameEn: 'Personal settings',
    pageKind: 'account-settings',
  },
  {
    key: '/account/notifications',
    path: '/account/notifications',
    name: '通知中心',
    nameEn: 'Notification center',
    pageKind: 'account-notifications',
  },
];

export function filterMenuItems(
  items: AppMenuItem[],
  access?: AppAccess,
  permissions: readonly PlatformPermission[] = [],
  isSuperAdmin = false,
) {
  return items.flatMap((item): AppMenuItem[] => {
    if (
      (item.access && item.access !== access) ||
      !hasPlatformPermission({ isSuperAdmin, permissions }, item.permission)
    ) {
      return [];
    }

    const children = item.children
      ? filterMenuItems(item.children, access, permissions, isSuperAdmin)
      : undefined;
    if (item.children && !children?.length) return [];
    return [{ ...item, children: children?.length ? children : undefined }];
  });
}

export function flattenRoutes(items: AppMenuItem[]): AppRouteMeta[] {
  return items.flatMap((item) => {
    if (item.children) {
      return flattenRoutes(item.children);
    }

    if (!item.path || !item.pageKind) {
      return [];
    }

    return [
      {
        id: item.key,
        label: item.name,
        labelEn: item.nameEn,
        locale: item.locale,
        path: item.path,
        pageKind: item.pageKind,
        closable: item.path !== defaultRoute.path,
        permission: item.permission,
      },
    ];
  });
}

export const appRouteTree: AppRouteConfig[] = [
  {
    path: '/user',
    layout: false,
    routes: [
      {
        path: '/user/login',
        name: '登录',
        locale: 'menu.login',
        component: lazyPage(() => import('@/pages/user/login')),
      },
      { path: '/user', redirect: '/user/login' },
      {
        path: '/user/forgot-password',
        name: '找回密码',
        component: lazyPage(() => import('@/pages/user/forgot-password')),
      },
      {
        path: '/user/register',
        name: '注册',
        component: lazyPage(() => import('@/pages/user/register')),
      },
      {
        path: '/user/register-result',
        name: '注册结果',
        component: lazyPage(() => import('@/pages/user/register-result')),
      },
      {
        path: '/user/mfa',
        redirect: '/user/verify?challenge=authenticator',
      },
      {
        path: '/user/verify',
        name: '登录验证',
        component: lazyPage(() => import('@/pages/user/mfa')),
      },
    ],
  },
  {
    path: '/exception/403',
    component: lazyPage(() => import('@/pages/exception/403')),
  },
  {
    path: '/account/center',
    component: lazyPage(() => import('@/pages/account/center/ProfileOverview')),
  },
  {
    path: '/account/settings',
    component: lazyPage(() => import('@/pages/account/settings')),
  },
  { path: '/account/profile', redirect: '/account/center' },
  { path: '/account/preferences', redirect: '/account/settings' },
  {
    path: '/account/notifications',
    component: lazyPage(() => import('@/pages/account/notifications')),
  },
  ...flattenRoutes(templatePageItems).map((route) => ({
    path: route.path,
    component: businessPages[route.path],
    permission: route.permission,
  })),
  { path: '/', redirect: defaultRoute.path },
  { path: '*', redirect: defaultRoute.path },
];

export const menuItems = templateNavigationItems;
export const pageItems = [...templatePageItems, ...accountPageItems];
export const appRoutes = flattenRoutes(pageItems);
export const publicRoutes = appRouteTree.filter((route) => route.layout === false);
export const protectedRoutes = appRouteTree.filter(
  (route) => route.layout !== false && route.path !== '/' && route.path !== '*',
);
export const rootRoute = appRouteTree.find((route) => route.path === '/');
export const fallbackRoute = appRouteTree.find((route) => route.path === '*');

export function findRoute(pathname: string) {
  return appRoutes.find((route) => route.path === pathname);
}
